import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button } from './components/ui/button';
import { Bold, Italic, Heading1, Heading2, List, ListOrdered, Strikethrough, Code, Quote } from 'lucide-react';

const RichTextEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
  });

  if (!editor) {
    return null;
  }

  return (
    <div className="border border-border rounded-md bg-card overflow-x-hidden flex flex-col h-full" style={{ width: '100%', maxWidth: '100%' }}>
      {/* Toolbar */}
      <div className="border-b border-border p-3 bg-muted/30 flex gap-1 flex-wrap overflow-x-hidden flex-shrink-0">
        <Button
          onClick={() => editor.chain().focus().toggleBold().run()}
          variant={editor.isActive('bold') ? 'default' : 'outline'}
          size="icon"
          className="transition-all-smooth hover-scale-110"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          variant={editor.isActive('italic') ? 'default' : 'outline'}
          size="icon"
          className="transition-all-smooth hover-scale-110"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'outline'}
          size="icon"
          className="transition-all-smooth hover-scale-110"
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'outline'}
          size="icon"
          className="transition-all-smooth hover-scale-110"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          variant={editor.isActive('bulletList') ? 'default' : 'outline'}
          size="icon"
          className="transition-all-smooth hover-scale-110"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          variant={editor.isActive('orderedList') ? 'default' : 'outline'}
          size="icon"
          className="transition-all-smooth hover-scale-110"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          variant={editor.isActive('strike') ? 'default' : 'outline'}
          size="icon"
          className="transition-all-smooth hover-scale-110"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleCode().run()}
          variant={editor.isActive('code') ? 'default' : 'outline'}
          size="icon"
          className="transition-all-smooth hover-scale-110"
        >
          <Code className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          variant={editor.isActive('blockquote') ? 'default' : 'outline'}
          size="icon"
          className="transition-all-smooth hover-scale-110"
        >
          <Quote className="h-4 w-4" />
        </Button>
      </div>

      {/* Editor */}
      <div className="p-4 overflow-x-hidden flex-1 flex flex-col">
        <EditorContent 
          editor={editor} 
          className="prose prose-sm max-w-none flex-1 overflow-y-auto overflow-x-hidden focus:outline-none [&>div]:min-h-full [&>div]:focus:outline-none [&>div]:prose-headings:font-semibold [&>div]:prose-p:my-2 [&>div]:prose-ul:my-2 [&>div]:prose-ol:my-2 [&>div]:prose-blockquote:border-l-4 [&>div]:prose-blockquote:border-border [&>div]:prose-blockquote:bg-muted/20 [&>div]:prose-blockquote:px-4 [&>div]:prose-blockquote:py-2 [&>div]:prose-code:bg-muted [&>div]:prose-code:px-1 [&>div]:prose-code:py-0.5 [&>div]:prose-code:rounded [&>div]:prose-code:text-sm"
          style={{ width: '100%', maxWidth: '100%' }}
        />
      </div>
    </div>
  );
};

export default RichTextEditor;